import React, {
  KeyboardEventHandler,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  InfoCircleRegularIcon,
  InfoCircleSolidIcon
} from '../../shared/icons/icons';
import Button from '../button/button';
import Popover from '../popover/popover';
import Typography from '../typography/typography';

import {
  bindEvent,
  unbindEvent
} from '../../shared/utils/event-utils/event-utils';
import generateUID from '../../shared/utils/generate-uid/generate-uid';

import './tooltip.scss';

interface ButtonProps {
  onClick: ((event: SyntheticEvent<Element, Event>) => void) | undefined;
  onKeyPress: KeyboardEventHandler<HTMLButtonElement> | undefined;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface TooltipProps {
  ariaLabel?: string;
  className?: string;
  content?: string;
  contentStyle?: object;
  hoverOver?: boolean;
  onClick?: () => void;
  popoverClassName?: string;
  render?: () => ReactNode;
  rootElementId?: string;
}

function Tooltip(props: TooltipProps) {
  const {
    'ariaLabel': ariaLabel,
    content,
    className = '',
    hoverOver,
    render,
    rootElementId = 'tooltip-root',
    popoverClassName,
    onClick
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const uid = useMemo(generateUID, []);
  const buttonRef = useRef<HTMLButtonElement>(null);

  function showTooltip() {
    let event;

    if (typeof Event === 'function') {
      event = new Event('tooltip');
    } else {
      event = document.createEvent('Event');
      event.initEvent('tooltip', false, false);
    }

    window.dispatchEvent(event);
    setIsOpen(true);
  }

  function hideTooltip() {
    setIsOpen(false);
  }

  function toggleTooltip(e) {
    e.preventDefault();
    e.stopPropagation();

    !hoverOver && onClick?.();
    if (!isOpen) {
      showTooltip();
    } else {
      hideTooltip();
    }
  }

  const buttonProps: ButtonProps = {
    onClick: !hoverOver ? toggleTooltip : undefined,
    onKeyPress: toggleTooltip
  };

  if (hoverOver) {
    buttonProps.onMouseEnter = showTooltip;
    buttonProps.onMouseLeave = hideTooltip;
  }

  const Icon =
    typeof render !== 'function' && isOpen
      ? InfoCircleSolidIcon
      : InfoCircleRegularIcon;

  useEffect(() => {
    function handleClickOutside(e) {
      if (!buttonRef.current?.contains(e.target)) {
        hideTooltip();
      }
    }
    bindEvent(window, ['scroll', 'resize', 'tooltip'], hideTooltip, true);
    bindEvent(window, ['click'], handleClickOutside);
    return () => {
      unbindEvent(window, ['scroll', 'resize', 'tooltip'], hideTooltip, true);
      unbindEvent(window, ['click'], handleClickOutside);
    };
  }, [buttonRef]);

  return (
    <>
      <Button
        aria-describedby={uid}
        aria-label={ariaLabel || 'tooltip'}
        className={`tooltip ${className}`}
        ref={buttonRef}
        styleName="button"
        {...buttonProps}
      >
        {typeof render === 'function' ? render() : <Icon styleName="icon" />}
      </Button>
      <Popover
        className={popoverClassName || 'tooltip__popover'}
        id={uid}
        isOpen={isOpen}
        maxWidth={335}
        rootElementId={rootElementId}
        styleName="popover"
        triggerRef={buttonRef}
        verticalOffset={3}
      >
        <Typography
          aria-live="polite"
          className="tooltip__content"
          styleName="content"
        >
          {content}
        </Typography>
      </Popover>
    </>
  );
}

export default Tooltip;
